import { Fragment, useEffect, useState } from 'react';
import { ChecklistItem, Job, Task, TaskType } from '../../../../clients/quipli-service/job-tasks/job-tasks';
import { Order, OrderMedia } from '../../../../clients/quipli-service/order/order';
import { Button } from '../../../../components/form/button';
import { useJobTaskApi } from '../../../../clients/quipli-service/job-tasks/job-task-api';
import { Product, ProductInventoryUnit } from '../../../../clients/quipli-service/product/product';
import { capitalize } from 'lodash';
import { Typography } from '../../../../components/typography/typography';
import Icon from '../../../../components/icons/icon';
import { Spinner } from '../../../../components/spinner/spinner';
import './inspection-modal.scss';
import classNames from 'classnames';
import { FileField } from '../../../../components/field/field';
import { Collapsible } from '../../../../components/collapsible/collapsible';
import { MediaCard } from '../../../order/view/order-media-list';

// Tasks
import { ChecklistTask } from '../components/tasks/checklist-task';
import { HoursFuelTask } from '../components/tasks/hours-fuel-task';
import { MediaTask } from '../components/tasks/media-task';

export type TaskProps = {
  task: Task;
  jobId: string;
  product: Product;
  unit: ProductInventoryUnit;
  unitTrackingVia: 'serialNumber' | 'unitNumber';
  next: () => void;
  back: () => void;
  isPreview?: boolean;
  setJob: (job: Job) => void;
  order?: Order;
  isFirstTask?: boolean;
  isLastTask?: boolean;
};

/**
 * Inspection modal content.
 * @param job - The job to inspect.
 * @param product - The product to inspect.
 * @param unit - The unit to inspect.
 * @param unitTrackingVia - The unit tracking via to use.
 * @param fuelUnit - The fuel unit to use.
 * @param close - The function to close the modal.
 * @param setJob - The function to set the job.
 * @param order - The order to inspect.
 */
export const InspectionModalContent = ({
  job,
  product,
  unit,
  unitTrackingVia,
  fuelUnit,
  close,
  setJob,
  order,
}: {
  job: Job;
  product: Product;
  unit: ProductInventoryUnit;
  unitTrackingVia: 'serialNumber' | 'unitNumber';
  fuelUnit: 'gallons' | 'liters';
  close: () => void;
  setJob: (job: Job) => void;
  order?: Order;
}) => {
  const { updateTask } = useJobTaskApi();

  const [step, setStep] = useState<number>();
  const [tasks, setTasks] = useState<Task[]>();

  useEffect(() => {
    const checklistTask = job.tasks.find(
      (t) => t.metadata?.productUnitId === unit.id && t.type === TaskType.InspectionChecklist,
    );
    const hoursTask = job.tasks.find(
      // task only included if unit has hour tracking or fuel product
      (t) => t.metadata?.productUnitId === unit.id && t.type === TaskType.MachineHours,
    );
    const mediaTask = job.tasks.find((t) => t.metadata?.productUnitId === unit.id && t.type === TaskType.MediaUpload);

    const enabledTasks = [checklistTask, hoursTask, mediaTask].filter((t) => t !== undefined);

    setTasks(enabledTasks as Task[]);

    if (step === undefined) {
      for (let i = 0; i <= enabledTasks.length - 1; i++) {
        const task = enabledTasks[i];
        if (task?.completedAt) continue; // skip completed tasks

        setStep(i);
        break;
      }
    }

    const initialTask = enabledTasks[0];
    if (initialTask && (initialTask.status === 'NotStarted' || !initialTask.startedAt)) {
      updateTask(job.id, initialTask.id, 'InProgress', initialTask.metadata ?? {});
    }
  }, [job, unit]);

  // prepare for next task after updateTask is called for the current task
  const next = async () => {
    if (!tasks || step === undefined) return;
    const task = tasks[step];
    if (!task || !task.metadata) return;

    const newStep = step + 1;
    if (newStep > tasks.length - 1) {
      close();
      return;
    }

    if (tasks[newStep]) {
      await updateTask(job.id, tasks[newStep]!.id, 'InProgress', tasks[newStep]!.metadata ?? {});
    }
    setStep(step + 1);
  };

  return tasks && step !== undefined ? (
    <div>
      {tasks[step]?.type === TaskType.InspectionChecklist && (
        <ChecklistTask
          task={tasks[step]}
          jobId={job.id}
          product={product}
          unit={unit}
          unitTrackingVia={unitTrackingVia}
          next={next}
          back={() => null}
          setJob={setJob}
          order={order}
          isFirstTask={step === 0}
          isLastTask={step === tasks.length - 1}
        />
      )}
      {tasks[step]?.type === TaskType.MachineHours && (
        <HoursFuelTask
          task={tasks[step]}
          jobId={job.id}
          jobType={job.type}
          product={product}
          unit={unit}
          unitTrackingVia={unitTrackingVia}
          fuelUnit={fuelUnit}
          next={next}
          back={() => (step - 1 >= 0 ? setStep(step - 1) : null)}
          setJob={setJob}
          isFirstTask={step === 0}
          isLastTask={step === tasks.length - 1}
        />
      )}
      {tasks[step]?.type === TaskType.MediaUpload && (
        <MediaTask
          task={tasks[step]}
          jobId={job.id}
          product={product}
          unit={unit}
          unitTrackingVia={unitTrackingVia}
          next={next}
          back={() => setStep(step - 1)}
          order={order}
          setJob={setJob}
          isFirstTask={step === 0}
          isLastTask={step === tasks.length - 1}
        />
      )}
    </div>
  ) : (
    <Spinner />
  );
};

export const InspectionModalNav = ({
  saveTask,
  isSaving,
  isFirstStep = false,
  isLastStep = false,
}: {
  saveTask: (forward: boolean) => void;
  isSaving?: boolean;
  isFirstStep: boolean;
  isLastStep: boolean;
}) => {
  return (
    <div>
      <div className="button-nav flex justify-between gap-3 mt-4">
        <Button
          disabled={isFirstStep}
          className="w-full button button-secondary"
          onClick={() => {
            saveTask(false);
          }}
        >
          Back
        </Button>
        <Button
          disabled={isSaving}
          className="w-full button button-yellow"
          onClick={() => {
            saveTask(true);
          }}
        >
          {isLastStep ? 'Save' : 'Next'}
        </Button>
      </div>
    </div>
  );
};

export const isTaskComplete = (task: Task, product: Product) => {
  if (!task || !task.metadata) return 'NotStarted';
  if (task.type === TaskType.InspectionChecklist) {
    return task.metadata?.checklist.every((item: ChecklistItem) => (item.required ? item.value !== undefined : true))
      ? 'Complete'
      : 'InProgress';
  } else if (task.type === TaskType.MachineHours) {
    return (!product.allowHourTracking || (product.allowHourTracking && task.metadata?.hours !== undefined)) &&
      ((product.fuelChargeProductId && task.metadata?.fuel !== undefined) || !product.fuelChargeProductId)
      ? 'Complete'
      : 'InProgress';
  } else {
    return task.metadata?.media && Object.keys(task.metadata.media).every((m) => m.length) ? 'Complete' : 'InProgress';
  }
};

export const MediaSection = ({
  list,
  isListLoading,
  refresh,
  upload,
  section,
  enableControls = true,
  isSaving,
}: {
  list: OrderMedia[];
  isListLoading: boolean;
  refresh: () => void;
  upload: (files: File[]) => void;
  section: string;
  enableControls?: boolean;
  isSaving?: boolean;
}) => {
  return (
    <div className="media-section">
      <Collapsible
        trigger={(isOpen) => (
          <div className="flex items-center gap-2 my-1">
            <Icon icon={isOpen ? 'ChevronUp' : 'ChevronDown'} size="small" onClick={() => 0} />
            <Typography weight="bold" type="text-sm">
              {capitalize(section)}
            </Typography>
          </div>
        )}
        openAtStart={true}
      >
        <div
          className={classNames('flex overflow-x-auto gap-[8px] w-full', {
            'justify-center': isListLoading,
            'mt-[8px] py-[4px]': list.length > 0,
          })}
        >
          <div className="upload-square ">
            <FileField
              multiple={true}
              accept="image/jpeg, image/png, video/mp4, video/quicktime"
              value={undefined}
              onChange={(f) =>
                upload([
                  ...(f instanceof Array
                    ? f.map((v) => {
                        return v;
                      })
                    : f
                    ? [f]
                    : []),
                ])
              }
              icon="Plus"
              showClickToUploadText={false}
              isSaving={isSaving}
            />
          </div>

          {isListLoading || !list ? (
            <Spinner />
          ) : (
            list.map((media, idx) => (
              <Fragment key={`order-media-${idx}`}>
                <MediaCard media={media} reload={refresh} enableControls={enableControls} />
              </Fragment>
            ))
          )}
        </div>
      </Collapsible>
    </div>
  );
};
